import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { useQuote } from "@/context/QuoteContext";
import { useLang } from "@/i18n/LanguageContext";
import { ArrowLeft, ArrowRight, PlayCircle, MapPin } from "lucide-react";

export default function VideoTestimonials() {
  const { openQuote } = useQuote();
  const { t } = useLang();
  const [clinics, setClinics] = useState([]);

  useEffect(() => {
    api.get("/clinics").then((r) => setClinics(r.data)).catch(() => {});
  }, []);

  const groups = useMemo(
    () =>
      clinics
        .map((c) => ({ id: c.id, name: c.name, location: `${c.city}, ${c.country}`, videos: c.videos || [] }))
        .filter((g) => g.videos.length),
    [clinics]
  );

  const total = groups.reduce((n, g) => n + g.videos.length, 0);

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-10">
      <Link to="/" className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-primary transition-colors mb-6" data-testid="video-back-home">
        <ArrowLeft className="w-4 h-4" /> {t("video.back")}
      </Link>

      <div className="text-center max-w-2xl mx-auto mb-12">
        <span className="inline-flex items-center gap-2 rounded-full bg-accent/10 text-accent px-3 py-1 text-xs font-semibold">
          <PlayCircle className="w-3.5 h-3.5" /> {t("video.badge")}
        </span>
        <h1 className="font-heading font-extrabold text-4xl sm:text-5xl mt-4 text-secondary">{t("video.title")}</h1>
        <p className="text-muted-foreground mt-4">
          {total > 0 ? t("video.subN", { n: total }) : t("video.sub")}
        </p>
      </div>

      {groups.length === 0 ? (
        <p className="text-center text-muted-foreground py-16" data-testid="video-empty">{t("video.loading")}</p>
      ) : (
        <div className="space-y-14">
          {groups.map((g) => (
            <section key={g.id} data-testid={`video-group-${g.id}`}>
              <div className="flex flex-wrap items-baseline justify-between gap-2 mb-5 border-b border-border pb-3">
                <div>
                  <h2 className="font-heading text-xl font-bold text-secondary">{g.name}</h2>
                  <span className="inline-flex items-center gap-1 text-sm text-muted-foreground"><MapPin className="w-3.5 h-3.5" /> {g.location}</span>
                </div>
                <Link to={`/clinics/${g.id}`} className="text-sm font-semibold text-primary hover:underline" data-testid={`video-clinic-link-${g.id}`}>
                  {t("common.viewClinic")}
                </Link>
              </div>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {g.videos.map((v, i) => (
                  <figure key={i} className="rounded-2xl overflow-hidden bg-white border border-border shadow-sm" data-testid={`video-card-${g.id}-${i}`}>
                    <div className="bg-black flex items-center justify-center">
                      <video src={v.url} poster={v.url.replace(/\.mp4$/, "-poster.jpg")} controls playsInline preload="metadata" className="w-full max-h-[420px] object-contain bg-black" data-testid={`video-player-${g.id}-${i}`} />
                    </div>
                    <figcaption className="p-4 text-sm text-foreground/85 border-t border-border">{v.title}</figcaption>
                  </figure>
                ))}
              </div>
            </section>
          ))}
        </div>
      )}

      <div className="mt-16 rounded-3xl bg-secondary text-white p-10 text-center relative overflow-hidden">
        <div className="absolute inset-0 opacity-10" style={{ backgroundImage: "radial-gradient(circle at 20% 30%, white 1px, transparent 1px)", backgroundSize: "24px 24px" }} />
        <div className="relative">
          <h2 className="font-heading text-2xl sm:text-3xl font-extrabold">{t("video.ctaTitle")}</h2>
          <p className="text-white/70 mt-3 max-w-lg mx-auto">{t("video.ctaSub")}</p>
          <Button onClick={() => openQuote({ type: "quote" })} className="rounded-full h-12 px-8 mt-6 bg-accent hover:bg-accent/90" data-testid="video-cta-quote">
            {t("common.getMyFreeQuote")} <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </div>
      </div>
    </div>
  );
}
